import * as React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from '../app/context'
import { Operation } from '../domain/Operation'
import Link from '../helper/link'
import Shorten from '../helper/shorten'
import { allowedDurationsForTracepoints, maxConcurrentTracepoints, planSupportsTracepoints } from '../helper/tracepoints'
import { WithEnvironmentAndService } from '../helper/WithEnvironmentAndService'
import Tracepoint from '../monitor/operation/tracepoint'
import { ApplicationState } from '../redux/ApplicationState'
import AddTracepointForm from '../monitor/operation/tracepoint-form/add-tracepoint-form'

interface OwnProps {
    operation: Operation
}

interface StateProps {
    tidewaysContext: TidewaysContext
}

type Props = OwnProps & StateProps

interface State {
    addTracepoint: boolean
    tracepointError: string | null
}

class OperationGraphHeader extends React.Component<Props, State> {
    public state: State = {
        addTracepoint: false,
        tracepointError: null,
    }

    public render() {
        if (this.state.addTracepoint) {
            return (
                <AddTracepointForm
                    operation={this.props.operation}
                    allowedDurations={allowedDurationsForTracepoints(this.props.tidewaysContext)}
                    // error={this.state.tracepointError}
                    onExit={() => this.setState({ addTracepoint: false })}
                />
            )
        }

        return (
            <WithEnvironmentAndService
                render={({ service, environment, isProductionEnvironment }) => (
                    <div className="box box-default">
                        <div className="box-header">
                            <h3 className="box-title">
                                <Shorten string={'Response Times'} />
                                {this.props.operation.active && planSupportsTracepoints(this.props.tidewaysContext) && (
                                    <Tracepoint
                                        maxConcurrent={maxConcurrentTracepoints(this.props.tidewaysContext)}
                                        onAdd={errorMessage => this.setState({ addTracepoint: true, tracepointError: errorMessage })}
                                        operation={this.props.operation}
                                        service={service}
                                        environment={environment}
                                    />
                                )}
                            </h3>
                        </div>
                        {this.props.tidewaysContext.application.license.transactionDetails ? (
                            <div className="box-body">
                                <div className="alert alert-info clearfix">
                                    {this.props.operation.active
                                        ? this.renderActiveTransactionVisibility(isProductionEnvironment, environment)
                                        : null}
                                    {this.props.operation.ignored ? this.renderIgnoredTransactionVisibility() : null}
                                    {!this.props.operation.active && !this.props.operation.ignored
                                        ? this.renderLimitedTransactionVisibility()
                                        : null}
                                </div>
                            </div>
                        ) : null}
                        {!this.props.tidewaysContext.application.license.transactionDetails ? (
                            <div className="box-body">
                                <div className="alert alert-info clearfix">
                                    <span>The license of this project does not include transaction-level monitoring.</span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            />
        )
    }

    private renderIgnoredTransactionVisibility() {
        return (
            <>
                <p>
                    <i className="fa fa-exclamation" /> This transaction is ignored. Collection of monitoring data, traces or exception data
                    is stopped.
                </p>

                <p>
                    To track it again change its visibility to &quot;
                    <Link
                        route="XhprofProfilerBundle.Transaction.activate"
                        params={{ operation: this.props.operation.id }}
                        query={{ origin: 'details' }}
                    >
                        Full
                    </Link>
                    &quot; or &quot;
                    <Link
                        route="XhprofProfilerBundle.Transaction.deactivate"
                        params={{ operation: this.props.operation.id }}
                        query={{ origin: 'details' }}
                    >
                        Limited
                    </Link>
                    &quot;.
                </p>
            </>
        )
    }

    private renderLimitedTransactionVisibility() {
        return (
            <>
                <div className="pull-right">
                    <Link
                        route="XhprofProfilerBundle.Transaction.activate"
                        params={{ operation: this.props.operation.id }}
                        query={{ origin: 'details' }}
                    >
                        Change Visibility to &quot;Full&quot; for this Transaction
                    </Link>
                </div>
                <p>
                    <i className="fa fa-exclamation" /> This transaction has limited visibility and is not collecting monitoring data, and
                    almost no traces, unless triggered through Chrome Extension, CLI or tracepoint.
                    {this.props.operation.parentId ? (
                        <span className="tw-ml-4">
                            Tagged transactions are limited by default even when the environment has Full visibility.
                        </span>
                    ) : null}
                </p>
            </>
        )
    }

    private renderActiveTransactionVisibility(isProductionEnvironment, environment) {
        return isProductionEnvironment ? (
            <span>The license of this project does not include transaction-level monitoring.</span>
        ) : (
            <span>
                Only production environments contain transaction-level monitoring. With a pro license you can configure{' '}
                <strong>{environment}</strong> to be an additional production environment.
            </span>
        )
    }
}

export default connect<StateProps, OwnProps>((globalState: ApplicationState, props: Props) => {
    return {
        tidewaysContext: globalState.tidewaysContext,
    }
})(OperationGraphHeader)
